import { authorizedApi } from './index';
import { Connection, PublicKey } from '@solana/web3.js';

const SOLANA_NETWORK = import.meta.env.VITE_SOLANA_RPC_URL;
const connection = new Connection(SOLANA_NETWORK, 'confirmed');

export const authorizedTokenApi = authorizedApi
  .enhanceEndpoints({
    addTagTypes: ['TokenBalance'],
  })
  .injectEndpoints({
    endpoints: (builder) => ({
      prepareTransfer: builder.mutation({
        query: ({
          network = 'solana',
          token,
          sourceWallet,
          destinationWallet,
          amount,
        }) => ({
          url: '/tokens/prepare-transfer',
          method: 'POST',
          body: {
            network,
            token,
            sourceWallet,
            destinationWallet,
            amount,
          },
        }),
      }),
      getTokenBalances: builder.query({
        queryFn: async ({ walletAddress, walletType }) => {
          try {
            if (!walletAddress) {
              return { data: [] };
            }

            const userPublicKey = new PublicKey(walletAddress);

            // Get token accounts from both SPL Token and Token-2022 programs
            const [splTokenAccounts, token2022Accounts] = await Promise.all([
              connection.getTokenAccountsByOwner(userPublicKey, {
                programId: new PublicKey(
                  'TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA',
                ),
              }),
              connection.getTokenAccountsByOwner(userPublicKey, {
                programId: new PublicKey(
                  'TokenzQdBNbLqP5VEhdkAS6EPFLC1PHnBqCXEpPxuEb',
                ),
              }),
            ]);

            // Get SOL balance for both external and embedded wallets
            const solBalance = await connection.getBalance(userPublicKey);

            // Combine both token account arrays
            const allTokenAccounts = [
              ...splTokenAccounts.value,
              ...token2022Accounts.value,
            ];

            // Extract raw balance data
            const responseData = [];

            // Track SOL balance to combine with wSOL if present
            let totalSolBalance = solBalance;

            allTokenAccounts.forEach((account) => {
              const parsedData = account.account.data;
              const mintAddress = new PublicKey(
                parsedData.slice(0, 32),
              ).toBase58();

              // Extract balance amount
              const data = account.account.data;
              const amountBuffer = data.slice(64, 72);
              const amount = new DataView(
                amountBuffer.buffer,
                amountBuffer.byteOffset,
                amountBuffer.byteLength,
              ).getBigUint64(0, true);

              // Only process accounts with non-zero amounts
              if (amount > 0) {
                if (
                  mintAddress === 'So11111111111111111111111111111111111111112'
                ) {
                  // Always combine wSOL with native SOL balance for both wallet types
                  totalSolBalance += Number(amount);
                } else {
                  responseData.push({
                    address: mintAddress,
                    rawAmount: amount.toString(), // Keep as string to preserve precision
                    isNative: false,
                  });
                }
              }
            });

            // Add combined SOL balance (native + wrapped) if it's greater than 0
            if (totalSolBalance > 0) {
              const rent =
                await connection.getMinimumBalanceForRentExemption(165);
              const totalSolBalanceWithBuffer = totalSolBalance - rent;
              if (totalSolBalanceWithBuffer > 0) {
                responseData.push({
                  address: 'So11111111111111111111111111111111111111112', // SOL mint address
                  rawAmount: totalSolBalanceWithBuffer.toString(),
                });
              }
            }

            return { data: responseData };
          } catch (error) {
            console.error('Error in getTokenBalances:', error);
            return {
              error: {
                status: 'CUSTOM_ERROR',
                data: error.message,
              },
            };
          }
        },
        // Cache configuration
        keepUnusedDataFor: 300, // Keep unused data in cache for 5 minutes
        refetchOnMountOrArgChange: false, // Don't refetch on mount if data exists
        refetchOnFocus: false, // Don't refetch on window focus
        refetchOnReconnect: true, // Refetch on reconnect to ensure fresh data
        providesTags: (result, error, { walletAddress, walletType }) =>
          result
            ? [
                { type: 'TokenBalance', id: `${walletAddress}:${walletType}` },
                { type: 'TokenBalance', id: 'LIST' },
              ]
            : [],
      }),
    }),
  });

export const {
  usePrepareTransferMutation,
  useGetTokenBalancesQuery,
  useLazyGetTokenBalancesQuery,
} = authorizedTokenApi;
